import { signal } from '@preact/signals';
import { getFile, type Entity } from './api.js';

/**
 * **Suivre les dérivés sans marteler l'API, et sans immobiliser l'écran.**
 *
 * Ce module remplace une ligne qui tenait les deux mauvais bouts à la fois
 * *(2026-09-12)* :
 *
 * ```ts
 * settled = await Promise.all(media.map((e) => waitReady(e.Fid!)));
 * ```
 *
 * Sur un lot de 56 photos — le cas rapporté —, elle lançait **56 sondages
 * concurrents toutes les 2 s, soit ≈ 28 requêtes par seconde**, contre un stage
 * d'API étranglé à **20**. D'où le `429` qu'a vu l'utilisateur. Et le commentaire
 * juste au-dessus annonçait d'attendre **le premier** média : le code les attendait
 * **tous**. La revue de coûts du 2026-08-17 l'avait relevé (trouvaille ③) sans que
 * personne ne fasse le lien avec l'erreur à l'écran.
 *
 * Le second défaut était pire que le premier : l'attente tenait `busy`, qui
 * **désactive tous les boutons** du site (`ui/parts.tsx`). Pendant que 56 dérivés
 * se construisaient, l'écran affichait « traitement en cours » et refusait tout
 * geste — « on attend sans rien voir bouger ».
 *
 * ### Trois propriétés, et chacune corrige l'un des deux défauts
 *
 * ① **Un seul sondage en vol, espacé.** Le débit ne dépend donc **pas** du nombre
 *    de fichiers : c'est la propriété qui rend le `429` impossible, et non un
 *    réglage qu'un lot plus gros ferait sauter. Un lot de 56 coûte le même débit
 *    qu'un lot de 2.
 * ② **Chaque entité est adoptée dès qu'elle se pose**, sans attendre les autres.
 *    La grille se remplit donc au fil de l'eau — ce que `Promise.all` interdisait
 *    structurellement, puisqu'il ne rend qu'une fois tout terminé.
 * ③ **`busy` n'est jamais tenu.** Le suiveur tourne détaché ; le compte de ce qui
 *    reste est publié dans un signal, que l'écran montre sans rien bloquer.
 *
 * ### Pourquoi une file partagée et non un suiveur par lot
 *
 * Deux envois qui se chevauchent doubleraient le débit, et trois le tripleraient :
 * la garantie ① ne tiendrait que pour un utilisateur qui attend sagement. La file
 * est donc **unique et de module**, et un second envoi y verse ses `fid` au lieu
 * de démarrer sa propre boucle.
 */

/**
 * Combien d'entités attendent encore leurs dérivés. **Ne bloque rien** : c'est un
 * compte.
 *
 * **Il inclut l'entité en cours de sondage**, et ce détail est tout le sujet
 * *(corrigé le 2026-09-12, même jour que sa livraison)*. Le compte publié était
 * `queue.length`, or le suiveur **sort** l'entité de la file pour la sonder : sur une
 * seule vidéo en traitement, la boucle faisait donc `1 → 0 → 1 → 0` toutes les
 * ~400 ms. Le bandeau, qui ne s'affiche qu'au-dessus de zéro, disparaissait et
 * réapparaissait au même rythme — et comme il vit dans le flux du document, **toute
 * la grille sautait avec lui**, pendant les quatre-vingt-dix secondes d'un
 * transcodage.
 *
 * Publié par `publier()` seul, et les signaux ignorent une écriture de valeur
 * identique : le compte ne **descend** donc qu'au moment où quelque chose se pose,
 * et ne produit aucun rendu entre deux sondages.
 */
export const processing = signal<number>(0);

/**
 * L'écart entre deux sondages, **le seul réglage qui borne le débit**.
 *
 * 250 ms ⇒ **4 req/s au pire**, quel que soit le nombre de fichiers, contre les
 * 20 req/s du stage. La marge est délibérément large : ce chemin n'est pas seul à
 * parler à l'API pendant un import — le miroir, les vignettes et les URL de lot y
 * passent aussi.
 */
const SPACING_MS = 250;

/**
 * Au-delà, on cesse de suivre **cette** entité.
 *
 * Ce n'est pas un échec : le job peut encore aboutir côté serveur. C'est la limite
 * de ce que le client accepte de surveiller, et la tuile reste alors sur son état
 * réel jusqu'au prochain rafraîchissement — il n'y a **aucune** resynchronisation
 * automatique dans ce site, le rafraîchissement est un bouton.
 */
const DEADLINE_MS = 120_000;

/** Les quatre gestes du suiveur, **injectables** — c'est ce qui le rend mesurable sans pile. */
export interface FollowPorts {
  readonly get: (fid: string) => Promise<Entity>;
  readonly adopt: (entity: Entity) => void | Promise<void>;
  readonly wait: (ms: number) => Promise<void>;
  readonly now: () => number;
}

const LIVE: FollowPorts = {
  get: getFile,
  adopt: () => {},
  wait: (ms) => new Promise((r) => setTimeout(r, ms)),
  now: () => Date.now(),
};

/** Un état terminal : le serveur a fini, en bien ou en mal. Les deux s'affichent. */
const settled = (entity: Entity): boolean =>
  entity.State === 'READY' || entity.State === 'FAILED';

interface Pending {
  readonly fid: string;
  /** L'instant au-delà duquel on arrête de suivre **celle-ci**. */
  readonly until: number;
}

let queue: Pending[] = [];
/**
 * L'entité **hors de la file parce qu'on la sonde**. Elle compte dans le total : sans
 * elle, le compte retombe à zéro entre deux sondages et l'écran clignote.
 */
let enVol: Pending | undefined;
let ports: FollowPorts = LIVE;
let loop: Promise<void> | undefined;

/**
 * **Le seul endroit qui écrit le compte**, et il le calcule en entier.
 *
 * L'unicité est la garantie : quatre sites d'écriture publiaient `queue.length`, et
 * il suffisait qu'un seul oublie l'entité en vol pour que l'écran clignote. Un
 * calcul, un appelant, aucun oubli possible.
 */
const publier = (): void => {
  processing.value = queue.length + (enVol ? 1 : 0);
};

/**
 * Met des `fid` sous surveillance et rend la main **tout de suite**.
 *
 * Rien n'est attendu ici : c'est le point de la manœuvre. L'appelant a déjà adopté
 * les entités dans leur état du moment, et le suiveur les remplacera une par une.
 *
 * Rend la boucle en cours pour que les **mesures** puissent l'attendre. Le code de
 * production ne s'en sert pas — et ne doit pas s'en servir, sans quoi il
 * réintroduirait l'attente qu'on vient de retirer.
 */
export function followDerivatives(
  fids: readonly string[],
  overrides?: Partial<FollowPorts>,
): Promise<void> {
  if (overrides) ports = { ...LIVE, ...overrides };
  const until = ports.now() + DEADLINE_MS;

  for (const fid of fids) {
    // Un `fid` déjà suivi ne se dédouble pas : deux entrées pour la même entité
    // doubleraient son coût de sondage sans rien apprendre de plus.
    if (!queue.some((p) => p.fid === fid) && enVol?.fid !== fid) queue.push({ fid, until });
  }
  publier();

  loop ??= run().finally(() => {
    loop = undefined;
  });
  return loop;
}

async function run(): Promise<void> {
  while (queue.length > 0) {
    // **Défiler par la tête, remettre par la queue** : une entité encore en
    // traitement ne doit pas repasser devant les autres, sinon une vidéo longue
    // affamerait les photos derrière elle.
    enVol = queue.shift()!;
    publier();

    if (ports.now() > enVol.until) {
      enVol = undefined;
      publier();
      continue;
    }

    let entity: Entity | undefined;
    try {
      entity = await ports.get(enVol.fid);
    } catch {
      // **Une lecture qui échoue ne sort pas de la file.** Un `429`, une coupure
      // réseau ou un `5xx` sont transitoires ; abandonner au premier refus
      // laisserait précisément le sablier éternel qu'on cherche à éviter. Le
      // garde-fou contre la boucle infinie est l'échéance, pas le compteur
      // d'erreurs.
      queue.push(enVol);
      enVol = undefined;
      publier();
      await ports.wait(SPACING_MS);
      continue;
    }

    if (settled(entity)) {
      enVol = undefined;
      publier();
      await ports.adopt(entity);
    } else {
      // **Le total ne bouge pas ici** : l'entité quitte le vol et rentre en file,
      // et `publier()` rend la même valeur — donc aucun rendu. C'est ce qui
      // empêche l'écran de sauter à chaque sondage.
      queue.push(enVol);
      enVol = undefined;
      publier();
    }

    // **L'espacement s'applique même sur une entité qui vient de se poser** : ce
    // qui est borné est le débit du suiveur, pas son taux d'échec.
    if (queue.length > 0) await ports.wait(SPACING_MS);
  }
  enVol = undefined;
  publier();
}

/** Remet le module à zéro — **pour les mesures**, qui partagent un processus. */
export function resetFollower(): void {
  queue = [];
  enVol = undefined;
  ports = LIVE;
  loop = undefined;
  processing.value = 0;
}
